"use client";

import { useMemo, useState } from "react";
import type { Departure } from "@/lib/dto";
import { formatMoney, cn } from "@/lib/format";
import { Button } from "@/components/ui/Button";
import {
  MonthYearFilter,
  type MonthOption,
} from "@/components/trip/MonthYearFilter";

const MONTH_ABBR_FMT = new Intl.DateTimeFormat("en-US", { month: "short" });
const MONTH_LONG_FMT = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric",
});

// Matches the home DepartureTrips widget's "7th Aug" ordinal-day format.
function ordinalDay(day: number): string {
  if (day % 10 === 1 && day !== 11) return `${day}st`;
  if (day % 10 === 2 && day !== 12) return `${day}nd`;
  if (day % 10 === 3 && day !== 13) return `${day}rd`;
  return `${day}th`;
}

function formatOrdinalDate(iso: string): string {
  const d = new Date(iso);
  return `${ordinalDay(d.getDate())} ${MONTH_ABBR_FMT.format(d)}`;
}

function safeDate(value: string): Date | null {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

type Props = {
  departures: Departure[];
  packagesPath: string;
};

/**
 * Client island for the `fix-departure-trip` article: a year selector + month
 * rail (shared `MonthYearFilter`, same grouping as `DeparturesPanel`) over a
 * list of departures rendered with the home `DepartureTrips` row markup. Data
 * is server-fetched and passed in — the filtering is pure client state, no
 * extra requests.
 */
export function FixDepartureTripClient({ departures, packagesPath }: Props) {
  const months = useMemo<MonthOption[]>(() => {
    const grouped = new Map<string, MonthOption>();
    for (const d of departures) {
      const s = safeDate(d.startDate);
      if (!s) continue;
      const key = `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, "0")}`;
      const current = grouped.get(key);
      if (current) {
        current.count += 1;
      } else {
        grouped.set(key, {
          key,
          year: s.getFullYear(),
          month: s.getMonth(),
          label: key,
          count: 1,
        });
      }
    }
    return [...grouped.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [departures]);

  const [activeMonthKey, setActiveMonthKey] = useState<string>(
    months[0]?.key ?? "",
  );
  const activeMonth = months.find((m) => m.key === activeMonthKey) ?? months[0];
  const years = useMemo(
    () => [...new Set(months.map((m) => m.year))].sort((a, b) => a - b),
    [months],
  );

  const visibleDepartures = useMemo(() => {
    if (!activeMonth) return [];
    return departures
      .filter((d) => {
        const s = safeDate(d.startDate);
        return (
          s?.getFullYear() === activeMonth.year &&
          s.getMonth() === activeMonth.month
        );
      })
      .sort(
        (a, b) =>
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
      );
  }, [activeMonth, departures]);

  if (!activeMonth) {
    return (
      <div className="mt-8 rounded bg-paper px-4 py-10 text-center text-sm text-muted shadow-[0_0_10px] shadow-black/10 md:px-6">
        No departures available.
      </div>
    );
  }

  return (
    <div className="rounded bg-paper px-4 py-6 shadow-[0_0_10px] shadow-black/10 md:px-6 md:py-10">
      {/* Year tabs */}
      <label
        htmlFor="year-filter"
        className="block text-base font-medium text-headings"
      >
        Sort By:
      </label>
      {years.length > 1 ? (
        <div
          className="flex gap-2 mt-3"
          role="tablist"
          aria-label="Departure year"
        >
          {years.map((year) => {
            const selected = year === activeMonth.year;
            return (
              <button
                key={year}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => {
                  const firstMonth = months.find((m) => m.year === year);
                  if (firstMonth) setActiveMonthKey(firstMonth.key);
                }}
                className={cn(
                  "shrink-0 font-extrabold shadow-sm border ease-in transition-all delay-75 duration-100 relative px-4 py-1.5 text-xs after:absolute after:block after:left-1/2 after:h-[10px] after:w-[10px] after:-translate-x-1/2 after:rotate-45 after:border-b after:border-r after:border-black after:bg-[#ACE6C0] after:transition-all after:duration-100",
                  selected
                    ? "bg-[#ACE6C0] text-headings border-black rounded after:opacity-100 after:bottom-[-6px]"
                    : "text-muted hover:text-headings rounded-full border-black/30 after:opacity-0 after:bottom-[-2px]",
                )}
              >
                {year}
              </button>
            );
          })}
        </div>
      ) : null}

      {/* Month tabs */}
      <MonthYearFilter
        months={months}
        activeKey={activeMonthKey}
        onChange={setActiveMonthKey}
        className="mt-4 border-b border-line"
      />

      {visibleDepartures.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted">
          No departures in{" "}
          {MONTH_LONG_FMT.format(
            new Date(activeMonth.year, activeMonth.month, 1),
          )}
          .
        </p>
      ) : (
        <ul className="mt-6 [&>li+li+li]:border-t [&>li+li+li]:border-headings/10 [&>li:first-child]:bg-primary/10 [&>li:first-child]:shadow-[0_0_10px] [&>li:first-child]:shadow-black/10 [&>li]:rounded">
          {visibleDepartures.map((dep) => (
            <li key={dep.id}>
              <div className="grid grid-cols-1 items-center gap-y-1 px-6 py-6 md:grid-cols-6 md:gap-3 md:py-3">
                <div className="md:col-span-2">
                  <h4 className="line-clamp-2 text-[1.125rem] leading-[1.2] capitalize md:text-lg">
                    <a
                      href={`${packagesPath}/${dep.packageSlug}`}
                      className="text-headings hover:text-primary"
                    >
                      {dep.packageTitle}
                    </a>
                    {dep.duration ? (
                      <span className="text-sm text-headings/80 capitalize md:hidden">
                        {" "}
                        - {dep.duration} {dep.durationUnit}
                      </span>
                    ) : null}
                  </h4>
                </div>
                <div className="font-secondary md:col-span-2">
                  {dep.duration ? (
                    <span className="hidden text-sm text-muted capitalize md:block">
                      {dep.duration} {dep.durationUnit}
                    </span>
                  ) : null}
                  <span className="block text-sm font-semibold text-headings/80 md:text-base md:text-headings">
                    From&nbsp;{formatOrdinalDate(dep.startDate)} to{" "}
                    {formatOrdinalDate(dep.endDate)}
                  </span>
                </div>
                <div className="py-3 md:py-0">
                  <div className="leading-[1.25]">
                    <span className="text-base font-bold leading-[1.2] text-primary md:text-lg">
                      {formatMoney(dep.currentPrice, dep.currency)}
                    </span>
                    <span className="ml-1 block text-sm text-muted md:ml-0">
                      Per Person
                    </span>
                  </div>
                </div>
                <div className="lg:text-right">
                  <Button
                    href={`${packagesPath}/${dep.packageSlug}#departure-dates`}
                    variant="primary"
                    size="sm"
                    className="uppercase"
                  >
                    Join This Trip
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
